import { useCallback, useMemo, useRef } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMessages, faArrowDown } from '@fortawesome/pro-regular-svg-icons';
import auth from '@client/components/auth';
import { Post, Reply } from '../../../../generated/graphql';
import { communityAnalytics } from '../utils/communityAnalytics';
import { useCommunityComments } from '../hooks/useCommunityComments';
import { useAddReplyMutation } from '../hooks/useAddReply';
import { useDeleteReplyMutation } from '../hooks/useDeleteReply';
import { useToggleReactionReply } from '../hooks/useToggleReactionReply';
import { getReplyInDMHandler } from '../utils/replyInDM';
import { CommunityReplyCell } from './CommunityReplyCell';
import { AddReply } from './AddReply';
import { useCommentsScrollBehavior } from './useCommentsScrollBehavior';
import { useAutoFocus } from '@client/new/helpers/useAutoFocus';

const COMMENTS_MAX_HEIGHT_PX = 300;

type Props = {
  influencerId: number;
  communityId: number;
  postId: string;
  post: {
    author: { id: number };
    media: Post['media'];
  };
  isUserCommunityOwner: boolean;
  isDmFeatureEnabled: boolean;
  onBlock: (communityId: number, userId: number) => void;
};

export const CommunityCommentsSection = ({
  influencerId,
  communityId,
  postId,
  post,
  isUserCommunityOwner,
  isDmFeatureEnabled,
  onBlock,
}: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);

  const {
    data: comments,
    isInitialLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useCommunityComments(communityId, postId);

  const replies = useMemo<Reply[]>(
    () => (comments?.pages ?? []).flatMap((page) => page).reverse(),
    [comments],
  );

  const addReply = useAddReplyMutation(communityId, postId);
  const deleteReply = useDeleteReplyMutation(communityId, postId);
  const toggleReplyReaction = useToggleReactionReply(communityId);

  const {
    hasNewComment,
    markOwnSubmit,
    captureScrollBeforePagination,
    scrollToNewComment,
    handleScroll,
  } = useCommentsScrollBehavior({
    scrollRef,
    isInitialLoading,
    lastReplyId: replies[replies.length - 1]?.id,
    pagesCount: comments?.pages.length ?? 0,
    repliesCount: replies.length,
  });

  useAutoFocus({ ref: replyInputRef, preventScroll: true });

  const handleSubmitReply = useCallback(
    (text: string) => {
      markOwnSubmit();
      addReply.mutate({ text, post, influencerId });
    },
    [markOwnSubmit, addReply, post, influencerId],
  );

  const handleViewPreviousComments = useCallback(() => {
    captureScrollBeforePagination();
    fetchNextPage();
    communityAnalytics.trackViewPreviousComments();
  }, [captureScrollBeforePagination, fetchNextPage]);

  const hasReplies = replies.length > 0;

  return (
    <Box className="flex flex-col">
      {hasReplies && (
        <Box
          ref={scrollRef}
          onScroll={handleScroll}
          className="px-5 overflow-y-auto no-scrollbar"
          style={{ maxHeight: `${COMMENTS_MAX_HEIGHT_PX}px` }}
        >
          {hasNextPage && (
            <Box className="flex justify-center mb-3">
              <Button
                variant="text"
                className="text-gray-2"
                onClick={handleViewPreviousComments}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? (
                  <CircularProgress size={14} className="mr-3" />
                ) : (
                  <FontAwesomeIcon className="mr-3" icon={faMessages} />
                )}
                {isFetchingNextPage
                  ? 'Loading previous comments'
                  : 'View previous comments'}
              </Button>
            </Box>
          )}
          {replies.map((reply) => (
            <CommunityReplyCell
              key={reply.id}
              reply={reply}
              communityId={communityId}
              postId={postId}
              isUserCommunityOwner={isUserCommunityOwner}
              onDelete={() => {
                deleteReply.mutate(reply.id);
                communityAnalytics.trackCommentDeleted(reply, influencerId);
              }}
              onBlock={onBlock}
              onToggleReaction={(type) =>
                toggleReplyReaction.mutateAsync({
                  postId,
                  replyId: reply.id,
                  type,
                })
              }
              userEmail={reply.author.email}
              onReplyInDM={getReplyInDMHandler({
                author: reply.author,
                isOwner: isUserCommunityOwner,
                isAdminModerator: auth.isAdministrator(),
                influencerId,
                isInfluencer: auth.isInfluencer(),
                isDmFeatureEnabled,
              })}
            />
          ))}
        </Box>
      )}
      <Box className="px-5 relative">
        {hasNewComment && (
          <Button
            variant="contained"
            className="absolute rounded-[100px] w-[175px] h-[28px] top-[-40px] left-0 right-0 m-auto"
            startIcon={
              <FontAwesomeIcon icon={faArrowDown} className="pl-2.5 h-4" />
            }
            onClick={scrollToNewComment}
          >
            <span className="px-2">New Comment</span>
          </Button>
        )}
        <AddReply inputRef={replyInputRef} submit={handleSubmitReply} />
      </Box>
    </Box>
  );
};
